// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { InvoiceAgingBucket } from '@/billing/api/types';
import { FilterOption } from '@/components/filter-toggle-group';
import { DateRangeShortcut } from '@/constants/date-range-shortcut';
import {
  CheckCircleOutlineRounded,
  DoNotDisturbAltOutlined,
  EditOutlined,
  ListRounded,
  PaidOutlined,
  PendingOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  INVOICE_STATUS_LABELS,
  InvoiceStatusFilter,
} from '@/constants/invoice-status';
import { createElement } from 'react';

// Pagination: matches the charges listing so both pages feel consistent.
export const PAGE_SIZE = 20;

// Status toggle options for the top-of-page filter. Icons match the rest of
// the billing listings (all filled circle/list icons).
export const INVOICE_STATUS_FILTER_OPTIONS: FilterOption<InvoiceStatusFilter>[] =
  [
    {
      value: 'all',
      label: 'All',
      icon: createElement(ListRounded, { fontSize: 'small' }),
    },
    {
      value: 'DRAFT',
      label: INVOICE_STATUS_LABELS.DRAFT,
      icon: createElement(EditOutlined, { fontSize: 'small' }),
    },
    {
      value: 'OPEN',
      label: INVOICE_STATUS_LABELS.OPEN,
      icon: createElement(PendingOutlined, { fontSize: 'small' }),
    },
    {
      value: 'PARTIALLY_PAID',
      label: INVOICE_STATUS_LABELS.PARTIALLY_PAID,
      icon: createElement(PaidOutlined, { fontSize: 'small' }),
    },
    {
      value: 'PAID',
      label: INVOICE_STATUS_LABELS.PAID,
      icon: createElement(CheckCircleOutlineRounded, { fontSize: 'small' }),
    },
    {
      value: 'VOID',
      label: INVOICE_STATUS_LABELS.VOID,
      icon: createElement(DoNotDisturbAltOutlined, { fontSize: 'small' }),
    },
  ];

// Aging bucket filter — used by the Aging FilterMenu. 'current' includes
// DRAFT / PAID / VOID / not-yet-due OPEN invoices, which is why the label is
// Current rather than "On time" or similar.
export const AGING_BUCKET_VALUES: InvoiceAgingBucket[] = [
  'current',
  '1-30',
  '31-60',
  '61-90',
  '91+',
];

export const AGING_FILTER_LABELS: Record<InvoiceAgingBucket, string> = {
  current: 'Current',
  '1-30': '1\u201330 days',
  '31-60': '31\u201360 days',
  '61-90': '61\u201390 days',
  '91+': '91+ days',
};

// Short label used inside the due-date cell chip — drops the "days" suffix.
export const AGING_CHIP_LABELS: Record<
  Exclude<InvoiceAgingBucket, 'current'>,
  string
> = {
  '1-30': '1\u201330 days',
  '31-60': '31\u201360 days',
  '61-90': '61\u201390 days',
  '91+': '91+ days',
};

// Styling for the inline aging chip on overdue rows. Outlined vs filled
// indicates severity escalation at the 31-60 and 91+ thresholds.
export function agingChipSx(bucket: Exclude<InvoiceAgingBucket, 'current'>): {
  borderColor: string;
  color: string;
  backgroundColor: string;
} {
  switch (bucket) {
    case '1-30':
      return {
        borderColor: '#FFB020',
        color: '#B76E00',
        backgroundColor: 'transparent',
      };
    case '31-60':
      return {
        backgroundColor: '#FFB020',
        color: '#FFFFFF',
        borderColor: '#FFB020',
      };
    case '61-90':
      return {
        borderColor: '#D14343',
        color: '#B42318',
        backgroundColor: 'transparent',
      };
    case '91+':
      return {
        backgroundColor: '#D14343',
        color: '#FFFFFF',
        borderColor: '#D14343',
      };
  }
}

// Date range shortcuts shared between invoice-date and due-date popovers.
export const INVOICE_DATE_SHORTCUTS: DateRangeShortcut[] = [
  {
    label: 'Last 90 days',
    getDateRange: (today) => [
      today.minus({ days: 90 }),
      today.minus({ days: 1 }),
    ],
  },
  {
    label: 'Last 60 days',
    getDateRange: (today) => [
      today.minus({ days: 60 }),
      today.minus({ days: 1 }),
    ],
  },
  {
    label: 'Last 30 days',
    getDateRange: (today) => [
      today.minus({ days: 30 }),
      today.minus({ days: 1 }),
    ],
  },
  {
    label: 'Next 30 days',
    getDateRange: (today) => [today, today.plus({ days: 30 })],
  },
  {
    label: 'Next 60 days',
    getDateRange: (today) => [today, today.plus({ days: 60 })],
  },
  {
    label: 'Next 90 days',
    getDateRange: (today) => [today, today.plus({ days: 90 })],
  },
];
